import { DialogComponent } from '@theme/dialog';
import { renderPrice, normalizeLieferzeit } from '@theme/configurator-shared';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {object} LakenSize
 * @property {number} id
 * @property {string} size
 * @property {string} lieferzeit
 * @property {number} priceCents
 * @property {number | null} compareAtPriceCents
 * @property {boolean} available
 * @property {string} featuredImage
 */

/**
 * The Laken product's own real, merchant-configured Farbe swatch (same
 * swatch definition the main variant picker's Farbe buttons use — see
 * snippets/swatch.liquid) — a solid color OR a small swatch image, never
 * both. `null` when that Farbe option value has no swatch configured.
 * @typedef {{ color: string } | { image: string } | null} LakenSwatch
 */

/**
 * @typedef {object} LakenAddon
 * @property {string} lakenFarbe
 * @property {number} lakenDefaultVariantId
 * @property {number} lakenDefaultPriceCents
 * @property {number | null} lakenDefaultCompareAtPriceCents
 * @property {string} lakenFeaturedImage
 * @property {LakenSwatch} lakenSwatch
 * @property {LakenSize[]} lakenSizes
 */

/**
 * @typedef {object} LakenCard
 * @property {string} farbe
 * @property {string} bettwaescheTitle
 * @property {string} lakenFarbe
 * @property {number} lakenDefaultVariantId
 * @property {number} lakenDefaultPriceCents
 * @property {string} lakenFeaturedImage
 * @property {string} lifestyleImage
 * @property {string} bettwaescheVariantImage
 * @property {LakenSize[]} lakenSizes
 * @property {LakenAddon[]} addons
 */

/**
 * @typedef {object} ConfiguratorVariant
 * @property {number} id
 * @property {string} farbe
 * @property {string} kissenGroesse
 * @property {string} deckeGroesse
 * @property {string} lieferzeit
 * @property {number} priceCents
 * @property {number | null} compareAtPriceCents
 * @property {boolean} available
 */

/**
 * @typedef {object} PopupRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} popupRows
 * @property {HTMLElement} popupPrice
 * @property {HTMLButtonElement} popupSubmit
 * @property {HTMLElement} popupError
 * @property {HTMLButtonElement} closeButton
 */

/**
 * Its own dedicated `DialogComponent` subclass rather than markup nested
 * inside a shared `<dialog-component>` — see `assets/component.js`'s
 * `#updateRefs`/`#isDescendant`: refs are scoped to the NEAREST ancestor
 * `Component`, so this popup's `<dialog>`/dropdowns/price/buttons must live
 * inside a custom element of their own, or a shared wrapper would capture
 * them and `<laken-recommendation>` would never see its own refs.
 *
 * @extends DialogComponent<PopupRefs>
 */
class LakenRecommendationPopup extends DialogComponent {
  // Literal array (not a spread of the inherited ['dialog']) — simpler to
  // read and avoids relying on base/derived class field initialization order.
  requiredRefs = ['dialog', 'popupRows', 'popupPrice', 'popupSubmit', 'popupError'];

  /** @type {LakenCard | null} */
  #card = null;

  /** @type {{ kissenGroesse: string }[]} */
  #kissen = [];

  /** @type {{ deckeGroesse: string } | null} */
  #decke = null;

  /** @type {number} */
  #selectedLakenSizeId = 0;

  /**
   * Index into `#card.addons` for the currently-selected addon/Laken
   * product. Defaults to 0 (the card's default addon, same one the carousel
   * card face already shows) — switching addons only ever happens inside
   * this popup via the addon select, never on the card itself.
   * @type {number}
   */
  #selectedAddonIndex = 0;

  /** @type {ConfiguratorVariant[]} */
  #configuratorVariants = [];

  /** @type {string} */
  #farbe = '';

  /** @type {string} */
  #lieferzeitOrder = '';

  /**
   * Opens the popup, seeded from a snapshot of the live configurator's
   * current selection passed in by the caller. Edits made inside the popup
   * never write back to the configurator — this is a one-way, read-at-
   * open-time snapshot, per this feature's design. `lieferzeitOrder` is the
   * same merchant-configured fastest-to-slowest ranking list the live
   * configurator uses (its own `dataset.lieferzeitOrder`) — the popup has no
   * independent access to it, so the caller reads it from the live
   * `<configurator-variant-picker>` element and passes it through.
   * @param {LakenCard} card
   * @param {ConfiguratorVariant[]} configuratorVariants
   * @param {string} farbe
   * @param {{ kissenGroesse: string }[]} kissen
   * @param {{ deckeGroesse: string } | null} decke
   * @param {string} lieferzeitOrder
   */
  open(card, configuratorVariants, farbe, kissen, decke, lieferzeitOrder) {
    this.#card = card;
    this.#configuratorVariants = configuratorVariants;
    this.#farbe = farbe;
    this.#kissen = kissen;
    this.#decke = decke;
    this.#lieferzeitOrder = lieferzeitOrder;
    this.#selectedAddonIndex = 0;
    this.#selectedLakenSizeId = card.lakenDefaultVariantId;
    this.refs.popupError.hidden = true;
    this.refs.popupSubmit.disabled = false;
    this.#renderRows();
    this.#updatePrice();
    this.showDialog();
  }

  /**
   * Opens the dialog directly into its error state, for when the caller
   * couldn't resolve the data `open()` needs (e.g. the clicked card's Farbe
   * variant data failed to load — see laken-recommendation.js's
   * handleCtaClick). Rendering empty Kissen/Decke dropdowns with no
   * indication anything is wrong would let a shopper submit with nothing
   * selected; this surfaces the failure immediately instead, using the same
   * error element/message/`data-add-to-cart-error` convention handleSubmit's
   * catch path already uses — the caller doesn't pass a message since this
   * element (not the caller) owns that data attribute.
   */
  openWithError() {
    this.refs.popupRows.innerHTML = '';
    this.refs.popupPrice.textContent = '';
    this.refs.popupError.textContent = this.dataset.addToCartError ?? 'Something went wrong. Please try again.';
    this.refs.popupError.hidden = false;
    this.refs.popupSubmit.disabled = true;
    this.showDialog();
  }

  #renderRows() {
    this.refs.popupRows.innerHTML = '';
    if (!this.#card) return;

    /** @type {{ label: string, options: { value: string, text: string, disabled: boolean }[], selected: string, onChange: (value: string) => void }[]} */
    const bettwaescheSelects = [];

    // One dropdown per existing Kissen instance. If the configurator only has
    // 1 Kissen selected, this renders a single simplified select (no "add
    // another Kissen" affordance anywhere in this popup — that's intentional,
    // this popup never lets the shopper add NEW Kissen instances beyond
    // whatever the configurator already had selected at open-time).
    this.#kissen.forEach((instance, index) => {
      const options = [
        ...new Set(this.#configuratorVariants.filter((v) => v.farbe === this.#farbe).map((v) => v.kissenGroesse)),
      ];
      bettwaescheSelects.push({
        label:
          index === 0
            ? this.dataset.kissenLabel ?? 'Kissen'
            : (this.dataset.kissenNumberedLabel ?? 'Kissen {{ number }}').replace('{{ number }}', String(index + 1)),
        options: options.map((value) => ({ value, text: value, disabled: false })),
        selected: instance.kissenGroesse,
        onChange: (value) => {
          this.#kissen[index] = { kissenGroesse: value };
          this.#updatePrice();
        },
      });
    });

    // Decke dropdown, derived the same way the separate configurator's own
    // `#renderDeckeGroup` derives its options: filtered by Farbe + whichever
    // Kissen instance #deckePairingIndex() resolves (additional Kissen
    // instances never affect Decke eligibility for the ones that AREN'T the
    // pairing instance). If no Kissen instance has any Decke pairings, no
    // Decke select is added at all — not even a "Keine Decke" option —
    // matching the configurator's own early-return behavior for that case.
    const pairingIndex = this.#deckePairingIndex();
    const pairingKissenGroesse = this.#kissen[pairingIndex]?.kissenGroesse;
    const deckeOptions = [
      ...new Set(
        this.#configuratorVariants
          .filter((v) => v.farbe === this.#farbe && v.kissenGroesse === pairingKissenGroesse && v.deckeGroesse !== '')
          .map((v) => v.deckeGroesse)
      ),
    ];

    if (deckeOptions.length > 0) {
      bettwaescheSelects.push({
        label: this.dataset.deckeLabel ?? 'Decke',
        options: [
          ...deckeOptions.map((value) => ({ value, text: value, disabled: false })),
          { value: '', text: this.dataset.noDeckeLabel ?? 'Keine Decke', disabled: false },
        ],
        selected: this.#decke?.deckeGroesse ?? '',
        onChange: (value) => {
          this.#decke = value === '' ? null : { deckeGroesse: value };
          this.#updatePrice();
        },
      });
    }

    // Bettwäsche group: the variant's own native product image (not the
    // custom.variant_gallery lifestyle-photo metafield) as a shared
    // thumbnail, with every Kissen/Decke select stacked beside it in one row.
    this.refs.popupRows.append(
      this.#buildRowGroup({
        image: this.#card.bettwaescheVariantImage,
        imageAlt: this.#card.bettwaescheTitle,
        selects: bettwaescheSelects,
      })
    );

    // Addon picker: its own full-width row ABOVE the Laken thumbnail/size
    // row (not nested inside that row's fields column) — see
    // #buildAddonRow. Only rendered when the card has more than one addon;
    // switching addons re-runs #renderRows() entirely, since the Laken
    // row's thumbnail/size-select/selected-size all need to change
    // together with it. A single-addon card renders no addon row at all,
    // so today's single-addon flow looks and behaves exactly as before.
    const addon = this.#card.addons[this.#selectedAddonIndex] ?? this.#card.addons[0];

    if (this.#card.addons.length > 1) {
      this.refs.popupRows.append(
        this.#buildAddonRow({
          addons: this.#card.addons,
          selectedIndex: this.#selectedAddonIndex,
          onChange: (index) => {
            this.#selectedAddonIndex = index;
            const newAddon = this.#card.addons[index];
            this.#selectedLakenSizeId = newAddon.lakenDefaultVariantId;
            this.#renderRows();
            this.#updatePrice();
          },
        })
      );
    }

    // Laken group: its own featured image, own row, own size select.
    const sizeOptions = addon.lakenSizes.map((size) => ({
      value: String(size.id),
      text: size.available ? size.size : `${size.size} (${this.dataset.soldOutLabel ?? 'Ausverkauft'})`,
      disabled: !size.available,
    }));

    this.refs.popupRows.append(
      this.#buildRowGroup({
        image: addon.lakenFeaturedImage,
        imageAlt: addon.lakenFarbe,
        selects: [
          {
            label: this.dataset.lakenLabel ?? 'Spannbettlaken',
            options: sizeOptions,
            selected: String(this.#selectedLakenSizeId),
            onChange: (value) => {
              this.#selectedLakenSizeId = Number.parseInt(value, 10);
              this.#updatePrice();
            },
          },
        ],
      })
    );
  }

  /**
   * Builds one row: a shared thumbnail on the left, one or more labeled
   * `<select>`s stacked beside it on the right — the Bettwäsche row groups
   * Kissen (+Decke) selects under one image, the Laken row is the same shape
   * with a single select, so both share this one builder.
   * @param {{ image: string, imageAlt: string, selects: { label: string, options: { value: string, text: string, disabled: boolean }[], selected: string, onChange: (value: string) => void }[] }} config
   * @returns {HTMLElement}
   */
  #buildRowGroup({ image, imageAlt, selects }) {
    const row = document.createElement('div');
    row.className = 'laken-recommendation__popup-row';

    const img = document.createElement('img');
    img.className = 'laken-recommendation__popup-row-image';
    img.src = image;
    img.alt = imageAlt;
    img.loading = 'lazy';
    row.append(img);

    const fields = document.createElement('div');
    fields.className = 'laken-recommendation__popup-row-fields';
    for (const select of selects) {
      fields.append(this.#buildSelectField(select));
    }
    row.append(fields);

    return row;
  }

  /**
   * Builds the addon picker's own full-width row: a horizontally
   * scrollable `<fieldset class="variant-option variant-option--buttons
   * variant-option--swatches">` of radio-driven `.variant-option__button-
   * label`s (one per addon), each showing the addon's own product image as
   * a real theme color-swatch, followed by its Farbe as visible text —
   * reusing the theme's native variant-option/button-label/swatch CSS (see
   * snippets/variant-main-picker.liquid, snippets/swatch.liquid,
   * snippets/swatch-styles.liquid) rather than the `<select>` pattern
   * #buildSelectField uses, per this popup's design (an addon is a visual
   * choice, better shown as swatch buttons than a text dropdown). Its own
   * row (not nested inside the Laken row's fields column) so it can span
   * the popup's full width and scroll horizontally when there are more
   * addons than fit — see .laken-recommendation__popup-addon-row-scroll in
   * sections/laken-recommendation.liquid. This is a plain scrolling
   * container, not the theme's real `<overflow-list>` web component (which
   * needs a Declarative Shadow DOM template + a collapsing "show more"
   * button) — addon counts are small enough that a simple overflow-x
   * scroll covers the same "doesn't break layout when there are many"
   * need without porting that component's shadow-DOM structure into
   * JS-built markup.
   *
   * The swatch itself is built inline (not via `{% render 'swatch' %}`,
   * since this markup is JS-built, not Liquid) to exactly match that
   * snippet's own `swatch.color`/`swatch.image`-driven output: a `<span
   * class="swatch swatch--unscaled">` with `--swatch-background: rgb(...)`
   * or `url(...)` — see snippets/swatch.liquid's `swatch.color`/
   * `swatch.image` branches. This is the Laken product's own real,
   * merchant-configured Farbe swatch (`card.addons[].lakenSwatch`, resolved
   * in sections/laken-recommendation.liquid from `laken_variant.product
   * .options_with_values`) — the SAME swatches the theme's native Farbe
   * picker shows — not a photo of the product (that's `lakenFeaturedImage`,
   * still used for the Laken thumbnail elsewhere in this popup, just not
   * here). A `lakenSwatch` of `null` (no swatch configured for that Farbe
   * value) falls back to `swatch--unavailable`, matching the native
   * snippet's own fallback for the same case.
   *
   * The theme's own `.variant-option__button-label--has-swatch` mode
   * doesn't show visible text next to its swatch (only visually-hidden),
   * so the swatch span + a visible `__text` span are combined here into
   * the plain (non-`--has-swatch`) button-label mode instead, matching
   * this popup's "image followed by color name" design.
   *
   * The sliding `__pill` highlight and its `data-current-checked`/
   * `data-previous-checked` two-item-history state machine are normally
   * driven by assets/variant-picker.js's `VariantPicker` component — this
   * popup isn't a `<variant-picker>`, so that exact logic (checked-index
   * history, pill-width CSS var measurement) is ported inline here rather
   * than reusing the class, to keep the same animation without pulling in
   * that component's own section-morphing/URL-updating responsibilities,
   * which are irrelevant to this popup.
   * @param {{ addons: LakenAddon[], selectedIndex: number, onChange: (index: number) => void }} config
   * @returns {HTMLElement}
   */
  #buildAddonRow({ addons, selectedIndex, onChange }) {
    const row = document.createElement('div');
    row.className = 'laken-recommendation__popup-row laken-recommendation__popup-addon-row';

    const scroll = document.createElement('div');
    scroll.className = 'laken-recommendation__popup-addon-row-scroll';

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'variant-option variant-option--buttons variant-option--swatches';

    const legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = this.dataset.addonLabel ?? 'Variante';
    fieldset.append(legend);

    /** @type {HTMLInputElement[]} */
    const radios = [];
    /** @type {number[]} */
    let checkedIndices = [selectedIndex];

    const updatePillWidths = () => {
      const [currentIndex, previousIndex] = checkedIndices;
      const currentWidth = currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined;
      const previousWidth =
        previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined;

      if (currentWidth) {
        fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
      } else {
        fieldset.style.removeProperty('--pill-width-current');
      }

      if (previousWidth) {
        fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
      } else {
        fieldset.style.removeProperty('--pill-width-previous');
      }
    };

    addons.forEach((option, index) => {
      const label = document.createElement('label');
      label.className = 'variant-option__button-label';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'laken-recommendation-addon';
      input.value = String(index);
      input.setAttribute('aria-label', option.lakenFarbe);
      input.dataset.previousChecked = 'false';
      input.dataset.currentChecked = index === selectedIndex ? 'true' : 'false';
      if (index === selectedIndex) input.checked = true;

      const swatch = document.createElement('span');
      const swatchBackground = option.lakenSwatch
        ? 'color' in option.lakenSwatch
          ? `rgb(${option.lakenSwatch.color})`
          : `url(${option.lakenSwatch.image})`
        : null;
      swatch.className = `swatch swatch--unscaled variant-option__button-label__swatch${swatchBackground ? '' : ' swatch--unavailable'}`;
      if (swatchBackground) swatch.style.setProperty('--swatch-background', swatchBackground);

      const pill = document.createElement('span');
      pill.className = 'variant-option__button-label__pill';

      const text = document.createElement('span');
      text.className = 'variant-option__button-label__text';
      text.textContent = option.lakenFarbe;

      label.append(input, swatch, pill, text);
      fieldset.append(label);
      radios.push(input);
    });

    fieldset.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      const newIndex = radios.indexOf(target);
      if (newIndex === -1) return;

      const [currentIndex, previousIndex] = checkedIndices;
      if (currentIndex !== undefined) radios[currentIndex].dataset.previousChecked = 'false';
      if (previousIndex !== undefined) radios[previousIndex].dataset.previousChecked = 'false';

      checkedIndices.unshift(newIndex);
      checkedIndices.length = Math.min(checkedIndices.length, 2);

      const newCurrentIndex = checkedIndices[0];
      const newPreviousIndex = checkedIndices[1];
      radios[newCurrentIndex].dataset.currentChecked = 'true';
      if (newPreviousIndex !== undefined) {
        radios[newPreviousIndex].dataset.previousChecked = 'true';
        radios[newPreviousIndex].dataset.currentChecked = 'false';
      }

      updatePillWidths();
      onChange(newIndex);
    });

    scroll.append(fieldset);
    row.append(scroll);
    // Measure after the fieldset is in the document so offsetWidth is real.
    requestAnimationFrame(updatePillWidths);
    return row;
  }

  /**
   * One `<select>`, styled via this theme's existing native-select
   * convention (`.variant-option__select-wrapper`/`.variant-option__select`
   * + caret icon — see `snippets/variant-main-picker.liquid`) rather than a
   * bespoke select style, so it looks consistent with every other size/
   * variant dropdown in the theme.
   *
   * `label` is prefixed onto every option's own text (e.g. "Decke 135/200")
   * instead of rendered as a separate element above the select — a native
   * `<select>` only ever displays its currently-chosen `<option>` text in the
   * closed control, so this is the only way to get "Decke" to visually sit
   * inside the pill beside the value, matching this popup's design (a
   * standalone label above/beside the wrapper is a different look, not this
   * one). `aria-label` carries the bare label text for screen readers, so it
   * isn't repeated for every option every time the value changes.
   * @param {{ label: string, options: { value: string, text: string, disabled: boolean }[], selected: string, onChange: (value: string) => void }} config
   * @returns {HTMLElement}
   */
  #buildSelectField({ label, options, selected, onChange }) {
    const field = document.createElement('div');
    field.className = 'laken-recommendation__popup-field';

    const wrapper = document.createElement('div');
    wrapper.className = 'variant-option__select-wrapper';

    const select = document.createElement('select');
    select.className = 'variant-option__select';
    select.setAttribute('aria-label', label);
    for (const option of options) {
      const optionEl = document.createElement('option');
      optionEl.value = option.value;
      optionEl.textContent = `${label} ${option.text}`;
      optionEl.disabled = option.disabled;
      optionEl.selected = option.value === selected;
      select.append(optionEl);
    }
    select.addEventListener('change', () => onChange(select.value));

    const caret = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    caret.setAttribute('aria-hidden', 'true');
    caret.setAttribute('focusable', 'false');
    caret.setAttribute('class', 'icon icon-caret');
    caret.setAttribute('viewBox', '0 0 10 6');
    // Same path as this theme's shared caret icon (assets/icon-caret.svg /
    // snippets/icon.liquid's 'caret' case) — reproduced here rather than
    // rendered via `{% render 'icon' %}` since this markup is built in JS,
    // not Liquid.
    caret.innerHTML =
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor" />';

    wrapper.append(select, caret);
    field.append(wrapper);
    return field;
  }

  /**
   * Index of the first Kissen instance whose size has any Decke-paired
   * variant, for the current Farbe — or -1 if none do. Local port of
   * `assets/configurator-variant-picker.js`'s own `#deckePairingIndex()`:
   * that component pairs the Decke with whichever Kissen instance is
   * actually pairable, not always instance 0 (e.g. the 40/40 neck-pillow
   * size has no Decke pairing, so a later Kissen instance — added via
   * "+ Bettwäsche Set hinzufügen" — carries the Decke instead). This popup
   * has to re-derive it itself since it works from its own copy of the
   * configurator's state, not by calling the configurator's own methods.
   * See docs/superpowers/specs/2026-09-03-bettwaesche-set-for-unpairable-kissen-design.md.
   * @returns {number}
   */
  #deckePairingIndex() {
    return this.#kissen.findIndex((instance) =>
      this.#configuratorVariants.some(
        (v) => v.farbe === this.#farbe && v.kissenGroesse === instance.kissenGroesse && v.deckeGroesse !== ''
      )
    );
  }

  /**
   * Resolves a Kissen instance + optional Decke pairing to its matching
   * ConfiguratorVariant. Whichever instance `#deckePairingIndex()` returns
   * resolves against the current Decke selection (the combined Kissen+Decke
   * variant if one is selected, the bare variant for "Keine Decke"); every
   * other instance always resolves against the bare variant, since only one
   * instance can ever pair with a Decke at a time. Shared by
   * `#updatePrice()` and `handleSubmit()` so the price shown to the shopper
   * and what's actually submitted to cart can never silently diverge.
   * @param {{ kissenGroesse: string }} instance
   * @param {number} index
   * @returns {ConfiguratorVariant | undefined}
   */
  #resolveKissenVariant(instance, index) {
    const deckeGroesse = index === this.#deckePairingIndex() ? this.#decke?.deckeGroesse ?? '' : '';
    return this.#configuratorVariants.find(
      (v) => v.farbe === this.#farbe && v.kissenGroesse === instance.kissenGroesse && v.deckeGroesse === deckeGroesse
    );
  }

  /**
   * Computes the Lieferzeit of the slowest-ranked variant among this popup's
   * currently-resolved Kissen/Decke lines (the ones that will share one
   * _set_id in handleSubmit — the separate Laken line is never included,
   * matching its own no-_set_id opt-out). Mirrors
   * assets/configurator-variant-picker.js's #slowestLieferzeit exactly
   * (same ranking rule: unknown values rank slower than every known value),
   * operating on this popup's own resolved variants and its own
   * #lieferzeitOrder instead. Returns '' if no lines resolve or no ranking
   * order is known.
   * @returns {string}
   */
  #slowestLieferzeit() {
    /** @type {ConfiguratorVariant[]} */
    const resolved = [];
    this.#kissen.forEach((instance, index) => {
      const match = this.#resolveKissenVariant(instance, index);
      if (match) resolved.push(match);
    });

    if (resolved.length === 0) return '';

    // Normalized so a variant's lieferzeit metafield text still matches the
    // merchant's own lieferzeit_order setting even when one uses a plain
    // hyphen and the other an en/em dash, or one includes a leading
    // "Lieferzeit " label and the other doesn't — see
    // configurator-shared.js's normalizeLieferzeit, mirroring
    // configurator-variant-picker.js's own #slowestLieferzeit.
    const order = this.#lieferzeitOrder
      .split('\n')
      .map((value) => normalizeLieferzeit(value.trim()))
      .filter(Boolean);

    let slowest = resolved[0];
    let slowestRank = order.indexOf(normalizeLieferzeit(slowest.lieferzeit));

    for (const variant of resolved.slice(1)) {
      const rank = order.indexOf(normalizeLieferzeit(variant.lieferzeit));

      const isUnknown = rank === -1;
      const currentIsUnknown = slowestRank === -1;

      let variantIsSlower;
      if (isUnknown !== currentIsUnknown) {
        variantIsSlower = isUnknown;
      } else {
        variantIsSlower = rank > slowestRank;
      }

      if (variantIsSlower) {
        slowest = variant;
        slowestRank = rank;
      }
    }

    // Normalized so a value that includes the merchant's inconsistent
    // leading "Lieferzeit " label doesn't duplicate the "Lieferzeit" text
    // this method's own callers already prefix in the cart line property
    // (e.g. "Lieferzeit: Lieferzeit 2-5 Werktage").
    return normalizeLieferzeit(slowest.lieferzeit);
  }

  #updatePrice() {
    if (!this.#card) return;

    // Mirrors the separate configurator's own `#resolvedVariants`: whichever
    // Kissen instance `#deckePairingIndex()` resolves against the current
    // Decke selection (the combined Kissen+Decke variant when one is
    // selected, the bare variant for "Keine Decke"); every other Kissen
    // instance always resolves against the bare variant, since only one
    // instance can ever pair with a Decke at a time. This avoids
    // double-counting a separate bare-Kissen price plus a nonexistent
    // standalone-Decke-only price.
    /** @type {{ priceCents: number, compareAtPriceCents: number | null }[]} */
    const resolvedLines = [];
    this.#kissen.forEach((instance, index) => {
      const match = this.#resolveKissenVariant(instance, index);
      if (match) resolvedLines.push(match);
    });

    const addon = this.#card.addons[this.#selectedAddonIndex] ?? this.#card.addons[0];
    const lakenSize = addon.lakenSizes.find((s) => s.id === this.#selectedLakenSizeId);
    if (lakenSize) resolvedLines.push(lakenSize);

    const total = resolvedLines.reduce((sum, line) => sum + line.priceCents, 0);
    // Same "neutral contribution" rule as the main configurator's own
    // #priceTotals (assets/configurator-variant-picker.js): a line
    // contributes its own compareAtPriceCents to this sum only when it's
    // actually a discount, otherwise its regular price — so a non-sale
    // line never inflates or deflates whether this bundle-plus-Laken total
    // as a whole shows as on sale. See
    // docs/superpowers/specs/2026-08-27-configurator-sale-price-design.md.
    const compareAtTotal = resolvedLines.reduce((sum, line) => {
      const contribution =
        line.compareAtPriceCents && line.compareAtPriceCents > line.priceCents ? line.compareAtPriceCents : line.priceCents;
      return sum + contribution;
    }, 0);

    renderPrice(this.refs.popupPrice, total, compareAtTotal, this.dataset.moneyFormat ?? '', this.dataset.currency ?? 'USD');
  }

  /**
   * Submits the popup's current selection as a single atomic multi-item
   * /cart/add.js POST, but split into two groupings unlike the separate
   * configurator's own `handleAddToCart`: the Kissen (and Decke-combined,
   * where applicable) lines share one freshly-generated `_set_id` cart-line
   * property AND one shared `Lieferzeit` property (the slowest among them,
   * per #slowestLieferzeit), mirroring `configurator-variant-picker.js`'s
   * mechanism exactly — while the Laken line gets NO `_set_id` at all, since
   * it's a separate, ungrouped optional add-on layered on top of an existing
   * bundle purchase, not part of that bundle's own grouping contract. It
   * still gets its own independent `Lieferzeit`, sourced from its own
   * selected size's variant data rather than the bundle's shared value.
   */
  async handleSubmit() {
    if (!this.#card) return;

    /** @type {{ id: number, quantity: number, properties?: Record<string, string> }[]} */
    const items = [];

    // Uses #resolveKissenVariant(), shared with #updatePrice(): whichever
    // Kissen instance #deckePairingIndex() resolves against the current
    // Decke selection, every other instance always resolves against the
    // bare variant. Every line in this bundle grouping shares one Lieferzeit
    // value (the slowest among them), mirroring the separate configurator's
    // own handleAddToCart rule.
    const setId = crypto.randomUUID();
    const lieferzeit = this.#slowestLieferzeit();
    this.#kissen.forEach((instance, index) => {
      const match = this.#resolveKissenVariant(instance, index);
      if (match) {
        items.push({
          id: match.id,
          quantity: 1,
          properties: lieferzeit ? { _set_id: setId, Lieferzeit: lieferzeit } : { _set_id: setId },
        });
      }
    });

    // Laken line is intentionally added with no _set_id — it's a separate,
    // ungrouped optional add-on, not part of the Bettwäsche bundle contract.
    // It still gets its OWN Lieferzeit, sourced from its own selected size's
    // variant data rather than the bundle's shared #slowestLieferzeit() —
    // the Laken's own delivery time has no relation to the Kissen/Decke
    // set's, so there's nothing to rank it against.
    if (this.#selectedLakenSizeId) {
      const addon = this.#card?.addons[this.#selectedAddonIndex] ?? this.#card?.addons[0];
      const lakenSize = addon?.lakenSizes.find((size) => size.id === this.#selectedLakenSizeId);
      items.push(
        lakenSize?.lieferzeit
          ? {
              id: this.#selectedLakenSizeId,
              quantity: 1,
              properties: { Lieferzeit: normalizeLieferzeit(lakenSize.lieferzeit) },
            }
          : { id: this.#selectedLakenSizeId, quantity: 1 }
      );
    }

    if (items.length === 0) return;

    this.refs.popupSubmit.disabled = true;
    this.refs.popupError.hidden = true;

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: String(item.id),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || `Cart add failed with status ${response.status}`);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'laken-recommendation',
          sourceId: String(this.#card.lakenDefaultVariantId),
          itemCount: items.length,
          didError: false,
        },
      });

      this.closeDialog();
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.refs.popupError.textContent = this.dataset.addToCartError ?? 'Something went wrong. Please try again.';
      this.refs.popupError.hidden = false;
    } finally {
      this.refs.popupSubmit.disabled = false;
    }
  }
}

if (!customElements.get('laken-recommendation-popup')) {
  customElements.define('laken-recommendation-popup', LakenRecommendationPopup);
}
